import {FetchQueryOptions} from '@tanstack/query-core';
import {QueryKey} from '@tanstack/react-query';
import {Request} from 'express';
import {ParsedUrlQuery} from 'querystring';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {landingFactory} from '../factory/landingFactory';
import {
  Layout,
  LayoutType,
  Maybe,
  StaticLandingType,
} from '../graphql/generated/types';
import {CACHE_ONLY_CONFIG} from '../reactQuery/constant';
import {createQueryClient} from '../reactQuery/createQueryClient';
import {getDayOfWeekParamFromQuery} from './dayOfWeek/getDayOfWeekParamFromQuery';
import {getEventParamFromQuery} from './event/getEventParamFromQuery';
import {getFreeSeriesParamFromQuery} from './freeSeries/getFreeSeriesParamFromQuery';
import {getGenreParamFromQuery} from './genre/getGenreParamFromQuery';
import {getOriginalParamFromQuery} from './original/getOriginalParamFromQuery';
import {getRankingParamFromQuery} from './ranking/getRankingParamFromQuery';
import {getTodayNewParamFromQuery} from './todayNew/getTodayNewParamFromQuery';
import {getTodayUpParamFromQuery} from './todayUp/getTodayUpParamFromQuery';

type FactoryMethodName =
  | 'staticLandingTodayNewLayout'
  | 'staticLandingTodayUpLayout'
  | 'staticLandingOriginalLayout'
  | 'staticLandingEventLayout'
  | 'staticLandingRankingLayout'
  | 'staticLandingDayOfWeekLayout'
  | 'staticLandingFreeSeriesLayout'
  | 'staticLandingGenreLayout';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function queryStaticLandingLayout<Query, Variables = unknown>({
  ssrRequest,
  type,
  uidInfoList,
  query,
  cookie,
  isSubtab = false,
  screenUid,
  fetchQueryOptions = CACHE_ONLY_CONFIG,
}: {
  ssrRequest: Maybe<Request>;
  type: StaticLandingType;
  uidInfoList: string[];
  query?: ParsedUrlQuery;
  cookie?: string;
  isSubtab?: boolean;
  screenUid?: number;
  fetchQueryOptions?: Omit<
    FetchQueryOptions<Layout | null, unknown, Layout | null, QueryKey>,
    'queryKey' | 'queryFn'
  >;
}) {
  const {factoryMethod, queryInputGenerator, queryName} = map[type];

  const queryInput = {
    ...queryInputGenerator({uidInfoList, query}),
    ...(isSubtab && {
      type: LayoutType.Layout,
      screenUid,
    }),
  };

  const queryClient = createQueryClient();
  const factoryOptions = landingFactory[factoryMethod](
    {queryInput},
    undefined,
    {cookie, ssrRequest: ssrRequest || undefined}
  );

  const layout = await queryClient.fetchQuery({
    queryKey: factoryOptions.queryKey,
    queryFn: factoryOptions.queryFn,
    ...(IS_CLIENT_SIDE && {staleTime: 10000}),
    ...fetchQueryOptions,
  });

  // 기존 반환 형식 유지: {[queryName]: Layout}
  const data = {[queryName]: layout} as Query;
  return {data, queryName, queryClient, queryInput};
}

const map: {
  [key: /* StaticLandingType */ string]: {
    factoryMethod: FactoryMethodName;
    queryInputGenerator: ({
      uidInfoList,
      query,
    }: {
      uidInfoList: string[];
      query?: ParsedUrlQuery;
    }) => any;
    queryName: string;
  };
} = {
  [StaticLandingType.TodayNew]: {
    factoryMethod: 'staticLandingTodayNewLayout',
    queryInputGenerator: getTodayNewParamFromQuery,
    queryName: 'staticLandingTodayNewLayout',
  },
  [StaticLandingType.TodayUp]: {
    factoryMethod: 'staticLandingTodayUpLayout',
    queryInputGenerator: getTodayUpParamFromQuery,
    queryName: 'staticLandingTodayUpLayout',
  },
  [StaticLandingType.Original]: {
    factoryMethod: 'staticLandingOriginalLayout',
    queryInputGenerator: getOriginalParamFromQuery,
    queryName: 'staticLandingOriginalLayout',
  },
  [StaticLandingType.Event]: {
    factoryMethod: 'staticLandingEventLayout',
    queryInputGenerator: getEventParamFromQuery,
    queryName: 'staticLandingEventLayout',
  },
  [StaticLandingType.Ranking]: {
    factoryMethod: 'staticLandingRankingLayout',
    queryInputGenerator: getRankingParamFromQuery,
    queryName: 'staticLandingRankingLayout',
  },
  [StaticLandingType.DayOfWeek]: {
    factoryMethod: 'staticLandingDayOfWeekLayout',
    queryInputGenerator: getDayOfWeekParamFromQuery,
    queryName: 'staticLandingDayOfWeekLayout',
  },
  [StaticLandingType.FreeSeries]: {
    factoryMethod: 'staticLandingFreeSeriesLayout',
    queryInputGenerator: getFreeSeriesParamFromQuery,
    queryName: 'staticLandingFreeSeriesLayout',
  },
  [StaticLandingType.Genre]: {
    factoryMethod: 'staticLandingGenreLayout',
    queryInputGenerator: getGenreParamFromQuery,
    queryName: 'staticLandingGenreLayout',
  },
};
